import type { ReactNode } from "react"
import { EditorContent } from "@tiptap/react"

import type { BrandfetchData } from "@productlane/api/src/browser"
import type { FeedbackOrigin } from "@productlane/db/browser"
import type { FormattedAttachment } from "@productlane/ui"
import { cn } from "@productlane/lib/src/browser"
import { AttachmentsList, typographyVariants } from "@productlane/ui"

import type { Editor } from "@tiptap/react"
import { InsightEditorMenu } from "@/screens/app/threads/insight-editor-menu"
import { isThreadNoteEditable } from "../../../thread-context"
import { SlackStyleMessage } from "./base/slack-style-message"
import { DeviceInfoLine } from "./device-info-line"

interface ThreadContentEventProps {
	editor: Editor
	senderName?: string
	senderEmail?: string | null
	senderImage?: string | null
	timestamp: Date
	attachments?: FormattedAttachment[]
	hasText?: boolean
	children?: ReactNode
	origin?: FeedbackOrigin
	onHideNotes?: () => void
	companyName?: string | null
	companyLogoUrl?: string | null
	companyBrandfetchData?: BrandfetchData | null
	senderIsUnverified?: boolean
	deviceInfoText?: string
	hasAiChatTranscript?: boolean
}

function NoteEditor({ editor }: { editor: Editor }) {
	return (
		<EditorContent key="editor-content" editor={editor} id="editor-content">
			<InsightEditorMenu editor={editor} />
		</EditorContent>
	)
}

function StaticContent({ editor }: { editor: Editor }) {
	return (
		<div
			className={cn(
				"focus:outline-none",
				typographyVariants({ variant: "message", mode: "invert" }),
			)}
			dangerouslySetInnerHTML={{ __html: editor.getHTML() }}
		/>
	)
}

export function ThreadContentEvent({
	editor,
	senderName,
	senderEmail,
	senderImage,
	timestamp,
	attachments = [],
	hasText,
	children,
	origin,
	onHideNotes,
	companyName,
	companyLogoUrl,
	senderIsUnverified,
	deviceInfoText,
	hasAiChatTranscript,
}: ThreadContentEventProps) {
	const isEditable =
		origin !== undefined &&
		!(hasAiChatTranscript && hasText) &&
		isThreadNoteEditable({ origin, createdAt: timestamp.getTime() })

	return (
		<SlackStyleMessage
			showAvatar={!isEditable}
			senderName={senderName}
			senderEmail={senderEmail}
			senderImage={senderImage}
			timestamp={timestamp}
			variant={isEditable ? "notes" : "standard"}
			onHideNotes={isEditable ? onHideNotes : undefined}
			onClick={isEditable ? () => editor.commands.focus() : undefined}
			companyName={companyName}
			companyLogoUrl={companyLogoUrl}
			senderIsUnverified={senderIsUnverified}
			footer={
				!isEditable && deviceInfoText ? (
					<DeviceInfoLine text={deviceInfoText} />
				) : undefined
			}
		>
			{isEditable ? (
				<NoteEditor editor={editor} />
			) : (
				<StaticContent editor={editor} />
			)}
			{attachments.length > 0 && (
				<>
					{hasText && <hr className="my-3" />}
					<AttachmentsList
						attachments={attachments}
						enableViewer
						showPointer={false}
					/>
				</>
			)}
			{children}
		</SlackStyleMessage>
	)
}
